/**
 * Settlement handlers for MSW
 * Implements filtering, sorting, pagination for settlements list
 */

import { http, HttpResponse } from 'msw'
import { getMockData, toSettlementListItem, getSettlementTransactions } from '../data'
import {
  applyDelay,
  generateTrendSeries,
  handleErrorSimulation,
  processListRequest,
  createNotFoundResponse
} from '../utils'

// Settlement search fields
const SETTLEMENT_SEARCH_FIELDS = [
  'id',
  'settlementUuid',
  'merchantId',
  'reference'
]

// Settlement sort fields
const SETTLEMENT_SORT_FIELDS = [
  'settlementDate',
  'grossAmount',
  'netAmount',
  'transactionCount',
  'status',
  'createdAt'
]

// Filter metadata fields
const SETTLEMENT_FILTER_FIELDS = [
  { field: 'status', label: 'Status' },
  { field: 'settlementCycle', label: 'Settlement Cycle' },
  { field: 'currency', label: 'Currency' }
]

export const settlementHandlers = [
  /**
   * GET /api/bff/settlements/
   * List settlements with filtering, sorting, and pagination
   */
  http.get('/api/bff/settlements/', async ({ request }) => {
    const url = new URL(request.url)
    const searchParams = url.searchParams

    // Handle error simulation
    const errorResponse = handleErrorSimulation(searchParams, {}, '/api/bff/settlements/')
    if (errorResponse) return errorResponse

    // Apply delay simulation
    await applyDelay({ searchParams, preset: 'normal' })

    // Get mock data
    const { settlements, merchants } = getMockData()

    // Create merchant name lookup
    const merchantNameMap: Record<string, string> = {}
    for (const m of merchants) {
      merchantNameMap[m.id] = m.name
    }

    // Handle additional filters not covered by processListRequest
    let filteredSettlements = [...settlements]

    // Merchant ID filter
    const merchantId = searchParams.get('merchantId')
    if (merchantId) {
      filteredSettlements = filteredSettlements.filter(s => s.merchantId === merchantId)
    }

    // Wallet ID filter
    const walletId = searchParams.get('walletId')
    if (walletId) {
      filteredSettlements = filteredSettlements.filter(s => s.walletId === walletId)
    }

    // Settlement cycle filter
    const cycle = searchParams.get('cycle')
    if (cycle) {
      const cycles = cycle.split(',').map(c => c.trim())
      filteredSettlements = filteredSettlements.filter(s =>
        cycles.includes(s.settlementCycle)
      )
    }

    // Currency filter
    const currency = searchParams.get('currency')
    if (currency) {
      const currencies = currency.split(',').map(c => c.trim().toUpperCase())
      filteredSettlements = filteredSettlements.filter(s =>
        currencies.includes(s.currency.toUpperCase())
      )
    }

    // Process list request with filtering, sorting, pagination
    const result = processListRequest(filteredSettlements, searchParams, {
      searchFields: SETTLEMENT_SEARCH_FIELDS,
      filterConfig: {
        dateField: 'settlementDate',
        amountField: 'netAmount'
      },
      sortFields: SETTLEMENT_SORT_FIELDS,
      defaultSortBy: 'settlementDate',
      defaultSortOrder: 'desc',
      filterMetadataFields: SETTLEMENT_FILTER_FIELDS
    })

    // Check for validation errors
    if (!result.validation.valid) {
      return HttpResponse.json({
        error: {
          code: 'VALIDATION_ERROR',
          message: 'Invalid query parameters',
          details: result.validation.errors,
          timestamp: new Date().toISOString()
        }
      }, { status: 400 })
    }

    // Convert to list items
    const settlementListItems = result.items.map(s =>
      toSettlementListItem(s, merchantNameMap[s.merchantId])
    )

    // Build response
    return HttpResponse.json({
      settlements: settlementListItems,
      pagination: result.pagination,
      filters: result.filters,
      meta: result.meta
    })
  }),

  /**
   * GET /api/bff/settlements/summary/
   * Settlement summary statistics
   */
  http.get('/api/bff/settlements/summary/', async ({ request }) => {
    const url = new URL(request.url)

    // Handle error simulation
    const errorResponse = handleErrorSimulation(url.searchParams, {}, '/api/bff/settlements/summary/')
    if (errorResponse) return errorResponse

    // Apply delay
    await applyDelay({ preset: 'fast' })

    // Get mock data
    const { settlements, merchants } = getMockData()

    // Create merchant name lookup
    const merchantNameMap: Record<string, string> = {}
    for (const m of merchants) {
      merchantNameMap[m.id] = m.name
    }

    // Calculate totals
    const totalSettlements = settlements.length
    const totalGrossAmount = settlements.reduce((sum, s) => sum + s.grossAmount, 0)
    const totalFees = settlements.reduce((sum, s) => sum + s.fees.totalFees, 0)
    const totalNetAmount = settlements.reduce((sum, s) => sum + s.netAmount, 0)

    // Status distribution with amounts
    const statusData: Record<string, { count: number, amount: number }> = {}
    for (const s of settlements) {
      if (!statusData[s.status]) {
        statusData[s.status] = { count: 0, amount: 0 }
      }
      statusData[s.status]!.count += 1
      statusData[s.status]!.amount += s.netAmount
    }

    const statusDistribution = Object.entries(statusData).map(([status, data]) => ({
      status: status as 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled',
      count: data.count,
      amount: Number(data.amount.toFixed(2)),
      percentage: Number((data.count / totalSettlements * 100).toFixed(2))
    }))

    // Get upcoming settlements (next 7 days)
    const now = new Date()
    const next7Days = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)

    const upcomingSettlements = settlements
      .filter(s =>
        s.status === 'pending'
        && new Date(s.expectedDate) <= next7Days
        && new Date(s.expectedDate) >= now
      )
      .sort((a, b) => new Date(a.expectedDate).getTime() - new Date(b.expectedDate).getTime())
      .slice(0, 5)
      .map(s => toSettlementListItem(s, merchantNameMap[s.merchantId]))

    // Get recent settlements (last 7 days)
    const past7Days = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)

    const recentSettlements = settlements
      .filter(s =>
        s.status === 'completed'
        && s.completedAt
        && new Date(s.completedAt) >= past7Days
      )
      .sort((a, b) => new Date(b.completedAt!).getTime() - new Date(a.completedAt!).getTime())
      .slice(0, 5)
      .map(s => toSettlementListItem(s, merchantNameMap[s.merchantId]))

    // Calculate average settlement time (for completed settlements)
    const completedSettlements = settlements.filter(s => s.status === 'completed' && s.completedAt)
    let averageSettlementDays = 0
    if (completedSettlements.length > 0) {
      const totalDays = completedSettlements.reduce((sum, s) => {
        const created = new Date(s.createdAt)
        const completed = new Date(s.completedAt!)
        return sum + (completed.getTime() - created.getTime()) / (24 * 60 * 60 * 1000)
      }, 0)
      averageSettlementDays = Number((totalDays / completedSettlements.length).toFixed(1))
    }

    // Average settlement amount
    const averageSettlementAmount = totalSettlements > 0
      ? Number((totalNetAmount / totalSettlements).toFixed(2))
      : 0

    const trends = {
      totalSettlements: generateTrendSeries({ base: totalSettlements, points: 14, integer: true, volatility: 0.05 }),
      totalGrossAmount: generateTrendSeries({ base: totalGrossAmount, points: 14, precision: 2, volatility: 0.06 }),
      totalFees: generateTrendSeries({ base: totalFees, points: 14, precision: 2, volatility: 0.08 }),
      totalNetAmount: generateTrendSeries({ base: totalNetAmount, points: 14, precision: 2, volatility: 0.06 }),
      averageSettlementDays: generateTrendSeries({ base: averageSettlementDays, points: 14, precision: 1, volatility: 0.04 }),
      averageSettlementAmount: generateTrendSeries({ base: averageSettlementAmount, points: 14, precision: 2, volatility: 0.05 })
    }

    return HttpResponse.json({
      totalSettlements,
      totalGrossAmount: Number(totalGrossAmount.toFixed(2)),
      totalFees: Number(totalFees.toFixed(2)),
      totalNetAmount: Number(totalNetAmount.toFixed(2)),
      trends,
      statusDistribution,
      upcomingSettlements,
      recentSettlements,
      averageSettlementDays,
      averageSettlementAmount,
      meta: {
        requestTime: new Date().toISOString(),
        processingTimeMs: 0
      }
    })
  }),

  /**
   * GET /api/bff/settlements/:id
   * Get single settlement by ID
   */
  http.get('/api/bff/settlements/:id', async ({ params, request }) => {
    const url = new URL(request.url)
    const id = params.id as string

    // Handle error simulation
    const errorResponse = handleErrorSimulation(url.searchParams, {}, `/api/bff/settlements/${id}`)
    if (errorResponse) return errorResponse

    // Apply delay
    await applyDelay({ preset: 'fast' })

    // Get mock data
    const { settlements, merchants, wallets, transactions } = getMockData()

    // Find settlement by ID or settlementUuid
    const settlement = settlements.find(
      s => s.id === id || s.settlementUuid === id
    )

    if (!settlement) {
      return createNotFoundResponse('Settlement', id, `/api/bff/settlements/${id}`)
    }

    // Get merchant details
    const merchant = merchants.find(m => m.id === settlement.merchantId)
    if (merchant) {
      settlement.merchant = {
        id: merchant.id,
        merchantId: merchant.merchantId,
        merchantUuid: merchant.merchantUuid,
        name: merchant.name,
        legalName: merchant.legalName
      }
    }

    // Get wallet details
    const wallet = wallets.find(w => w.id === settlement.walletId)
    if (wallet) {
      settlement.wallet = {
        id: wallet.id,
        walletUuid: wallet.wallet_uuid,
        name: wallet.name,
        currency: wallet.currency
      }
    }

    // Get transaction details
    const settlementTransactions = getSettlementTransactions(
      settlement.transactions,
      transactions.map(t => ({
        id: t.id,
        chargeUuid: t.chargeUuid,
        amount: t.amount,
        createdAt: t.createdAt,
        status: t.status
      }))
    )

    return HttpResponse.json({
      settlement,
      transactions: settlementTransactions,
      meta: {
        requestTime: new Date().toISOString(),
        processingTimeMs: 0
      }
    })
  })
]
